// Human-readable report sections followed by every saved bundle decision.
export function csvCell(value: unknown): string {
  let text = value == null ? '' : String(value);
  if (typeof value === 'string' && /^[\s]*[=+@-]/.test(text)) text = "'" + text;
  return '"' + text.replace(/"/g, '""') + '"';
}
type SavedRecord = Record<string, any>;
const tierName = (tier: string | null) => tier ? ({boost:'Boost',hold:'Hold',explore:'Explore',excluded:'Excluded'}[tier] ?? tier) : 'Not previously assigned';
const rank: Record<string, number> = {excluded:0,explore:1,hold:2,boost:3};
const explanation: Record<string, string> = {
  confident_profit: 'Even the cautious estimate is profitable.',
  confident_loss: 'Even the optimistic estimate loses money.',
  undecided: 'The estimated profit range includes break-even.',
  no_band: 'Not enough reliable prediction data to judge profitability.',
  below_floor: 'Predicted installs are below the minimum required to judge profitability.',
  no_recent_install: 'No attributed MB install within the eligibility window.',
  no_prediction: 'No current prediction available.',
};
const numeric = (value: unknown, digits=2): number | string => typeof value === 'number' && Number.isFinite(value) ? Number(value.toFixed(digits)) : 'N/A';
const date = (value: unknown) => typeof value === 'string' && Number.isFinite(Date.parse(value)) ? new Date(value).toISOString().replace('T',' ').replace(/\.\d{3}Z$/, ' UTC') : 'N/A';
function movement(d: SavedRecord): string {
  if(d.tier==='excluded') return d.fromTier && d.fromTier!=='excluded' ? 'Removed' : 'Excluded';
  if(!d.fromTier) return 'New assignment';
  if(d.tier===d.fromTier) return 'Unchanged';
  return rank[d.tier]>rank[d.fromTier] ? 'Promoted' : 'Demoted';
}
export type ExportKind = 'summary' | 'changes' | 'decisions';
export function buildRunCsv(run: SavedRecord, decisions: SavedRecord[], kind: ExportKind = 'summary'): string {
  const summary=run.summary, settings=summary?.settings, coverage=summary?.coverage;
  const rows: unknown[][] = [
    ['CROSSROADS TIERING REPORT'],
    ['Run',run.id,'Result',run.status],
    ['Started',date(run.started_at),'Finished',date(run.finished_at)],
  ];
  if(run.error_message) rows.push(['Run issue',run.error_message]);
  if(summary) {
    rows.push(['Campaign',settings?.campaign,'Event',settings?.event],
      ['Analysis period',coverage?.fromDate,'Through',coverage?.toDate],
      ['Unique bundles in active tiers',summary.distinctBundles],
      ['How to read counts','Each bundle can have a different tier in each DMA. Tier bundle counts overlap and must not be added together.'],
      [],['TIER SUMMARY'],['Tier','Distinct bundles','Bundle/DMA assignments']);
    for(const tier of ['explore','hold','boost']) rows.push([tierName(tier),summary.counts?.[tier]?.bundles ?? 'N/A',summary.counts?.[tier]?.memberships ?? 'N/A']);
    rows.push([],['CHANGES SINCE THE PREVIOUS AUDITED RUN'],['Previous tier','Current tier','Bundle/DMA assignments']);
    for(const [path,count] of Object.entries(summary.movements ?? {})) {
      const [from,to]=path.split(' → ');
      rows.push([tierName(from==='new' ? null : from),tierName(to),count]);
    }
    if(!Object.keys(summary.movements ?? {}).length) rows.push(['No tier movements recorded']);
    rows.push(['New assignments',summary.newCount],['Changes between active tiers',summary.movedCount],['Removed from active tiers',summary.removedCount ?? 0],
      [],['CROSSROADS PUBLICATION'],['Seat','Tiers enabled for publishing','Result','Rows created','Rows updated','Rows unchanged','Rows failed','Issue']);
    for(const seat of settings?.seats ?? []) {
      const result=run.publication?.[seat.key];
      rows.push([seat.key.toUpperCase(),seat.enabledTiers.map(tierName).join(', '),result?.status ?? 'No completed publication recorded',result?.created ?? 'N/A',result?.updated ?? 'N/A',result?.unchanged ?? 'N/A',result?.failed ?? 'N/A',result?.error ?? '']);
    }
    rows.push(['Publication note','Tier decisions and publication are separate. A disabled tier is classified but is not sent to that seat. Seat results do not confirm each individual bundle.'],
      [],['DATA NOTES'],['Finding','Bundle/DMA pairs','Meaning'],
      ['No prediction',coverage?.missingPredictionCount,'Observed traffic without a matching prediction. These pairs receive no active tier.'],
      ['No recent install',coverage?.noRecentInstallCount,'Excluded by the install eligibility window.'],
      ['Predicted loss',coverage?.confirmedLossCount,'Excluded because even the optimistic estimate loses money.'],
      ['Missing cost',summary.missingCostCount,'Active assignments using zero because cost was missing. Profitability may be overstated.'],
      ['Recorded zero cost',summary.zeroCostCount,'Active assignments with a recorded cost of zero.'],
      [],['DECISION RULES'],
      ['Install eligibility window (days)',settings?.eligibilityDays ?? coverage?.eligibilityDays],
      ['Profitability window (days)',settings?.lookbackDays],
      ['Commission per install (USD)',numeric(settings?.payout)],
      ['Uncertainty multiplier',settings?.z,'Higher values widen the profit range. 1.96 is an approximate 95% interval under the model assumptions, not a guarantee.'],
      ['Minimum predicted installs',settings?.minExpectedConversions,'Below this threshold stays in Explore. Zero disables the minimum.']);
  } else {
    rows.push(['History available','Basic run summary only. Detailed bundle decisions were not saved.'],
      ['Tier','Bundle/DMA assignments'],['Explore',run.explore_count],['Hold',run.hold_count],['Boost',run.boost_count],
      ['Rows created',run.rows_created],['Rows updated',run.rows_updated],['Rows unchanged',run.rows_unchanged],['Rows failed',run.rows_failed]);
  }
  if(kind==='summary') return serialize(rows);
  const seats: SavedRecord[]=settings?.seats ?? [];
  const detailRows: unknown[][]=[['Run','App bundle','DMA / state','Change','Previous tier','Current tier','Why this decision?',
    'Predicted installs','Expected revenue (USD)','Cost used (USD)','Lowest estimated profit (USD)','Highest estimated profit (USD)','Cost note',
    ...seats.flatMap(seat=>[`${seat.key.toUpperCase()}: membership change under enabled tiers`,`${seat.key.toUpperCase()}: run publication result`])]];
  const selected=kind==='changes' ? decisions.filter(d=>d.fromTier!==d.tier) : decisions;
  const ordered=[...selected].sort((a,b)=>Number(movement(a)==='Unchanged')-Number(movement(b)==='Unchanged') || String(a.appBundle).localeCompare(String(b.appBundle)) || String(a.dma ?? a.state).localeCompare(String(b.dma ?? b.state)));
  for(const d of ordered) detailRows.push([
    run.id,d.appBundle,d.dma != null ? String(d.dma) : String(d.state ?? '').toUpperCase(),movement(d),tierName(d.fromTier),tierName(d.tier),explanation[d.reason] ?? 'No explanation saved.',
    numeric(d.expectedTotal),numeric(d.revenue),numeric(d.cost),numeric(d.marginLow),numeric(d.marginHigh),
    d.costMissing ? 'Missing cost; zero assumed. Profit may be overstated.' : d.cost===0 ? 'Recorded cost is zero.' : '',
    ...seats.flatMap(seat=>[seatChange(d,seat),run.publication?.[seat.key]?.status ?? 'No completed publication recorded']),
  ]);
  return serialize(detailRows);
}
function seatChange(d: SavedRecord, seat: SavedRecord): string {
  const before=seat.enabledTiers.includes(d.fromTier), after=seat.enabledTiers.includes(d.tier);
  if(!before && !after) return 'Outside enabled tiers';
  if(before && !after) return `Leaves ${tierName(d.fromTier)}`;
  if(!before && after) return `Enters ${tierName(d.tier)}`;
  return d.fromTier===d.tier ? `Stays in ${tierName(d.tier)}` : `${tierName(d.fromTier)} to ${tierName(d.tier)}`;
}
function serialize(rows: unknown[][]): string {
  const width=Math.max(...rows.map(row=>row.length));
  return '\uFEFF' + rows.map(row => [...row,...Array(width-row.length).fill('')].map(csvCell).join(',')).join('\r\n') + '\r\n';
}
export async function fetchAllDecisions(fetchPage: (offset: number) => Promise<{data: Record<string, unknown>[]; total: number}>): Promise<Record<string, unknown>[]> {
  const first = await fetchPage(0);
  const result = [...first.data];
  for (let offset = 50; offset < first.total; offset += 200) {
    const offsets = [offset, offset + 50, offset + 100, offset + 150].filter(n => n < first.total);
    const pages = await Promise.all(offsets.map(fetchPage));
    for (const page of pages) {
      if (page.total !== first.total) throw new Error('Run changed during export. Please retry.');
      result.push(...page.data);
    }
  }
  if (result.length !== first.total) throw new Error('Incomplete decision export. Please retry.');
  return result;
}
